import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * SlideDrawer — slide-over panel from the right edge.
 *
 * Replaces centered modal dialogs for library operations. The library
 * tree stays visible and interactive while the drawer is open.
 *
 * Uses CSS transitions for the slide animation and backdrop opacity.
 * Escape key and backdrop click close the drawer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
export function SlideDrawer({ open, title, onClose, children, footer, }) {
    // Animate open/close with a brief delay for CSS transitions
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const panelRef = useRef(null);
    useEffect(() => {
        if (open) {
            setMounted(true);
            // Delay to allow the closed state to render before transitioning to open
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setVisible(true));
            });
        }
        else {
            setVisible(false);
            const timer = setTimeout(() => setMounted(false), 250);
            return () => clearTimeout(timer);
        }
    }, [open]);
    // Escape to close
    useEffect(() => {
        if (!open)
            return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);
    // Prevent body scroll while open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = ''; };
        }
    }, [open]);
    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget)
            onClose();
    }, [onClose]);
    if (!mounted)
        return null;
    const state = visible ? 'open' : 'closed';
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "ac-drawer-overlay", "data-state": state, onClick: handleOverlayClick }), _jsxs("div", { ref: panelRef, className: "ac-drawer-panel", "data-state": state, role: "dialog", "aria-modal": "true", children: [_jsxs("div", { className: "ac-drawer-header", children: [_jsx("h2", { className: "ac-drawer-title", children: title }), _jsx("button", { className: "ac-drawer-close", onClick: onClose, children: "\u00D7" })] }), _jsx("div", { className: "ac-drawer-content", children: children }), footer && (_jsx("div", { className: "ac-drawer-footer", children: footer }))] })] }));
}
//# sourceMappingURL=SlideDrawer.js.map